import { Col } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAccordions } from '../../hooks/useAccordions';
import { useExternalLinks } from '../../hooks/useExternalLinks';
import { useKeywordsDefinition } from '../../hooks/useKeywordsDefinition';
import { keywordsType } from '../../stores/KeywordStore';
import { PageProps } from '../../stores/PageStore';
import { RootStoresContext } from '../../stores/RootStore';
import { cleanString } from '../../util/renderHelpers';
import ErrorPage from '../Error/ErrorPage';
import Loading from '../Loading/Loading';

type PageContentRHSProps = {
  children?: never;
  content: string;
  keywords: keywordsType | null;
};

const PageContentRHS: React.FC<PageContentRHSProps> = React.memo(({ content, keywords }) => {
  const pageContentRHSRef = useRef<HTMLDivElement>(null);
  // Modify conntent from matrix wyswig
  // Add external link icon
  useExternalLinks(pageContentRHSRef);
  // Split content find Accordion CCT inside content string from matrix, extract data from accordions, create state and display in react component
  const displayRHSContent = useAccordions(content);
  // Add keyword tooltip
  useKeywordsDefinition(pageContentRHSRef, keywords, displayRHSContent); // tooltip

  return (
    <div className="page__rhs" ref={pageContentRHSRef}>
      {displayRHSContent}
    </div>
  );
});

type PageContentProps = {
  children?: never;
  content: PageProps;
  keywords: keywordsType | null;
  col?: { xs?: number; lg?: number };
  type?: string;
};

const PageContent: React.FC<PageContentProps> = ({ content, col, keywords, type }) => {
  const pageContentRef = useRef<HTMLDivElement>(null);
  // Modify conntent from matrix wyswig
  // Add external link icon
  useExternalLinks(pageContentRef);
  // Split content find Accordion CCT inside content string from matrix, extract data from accordions, create state and display in react component
  const displayContent = useAccordions(content.pageContent);
  // Add keyword tooltip
  useKeywordsDefinition(pageContentRef, keywords, displayContent);

  return content.pageRHS ? (
    <>
      <Col xs={{ span: col && col.xs }} lg={{ span: 16 }}>
        <div className="page__content rhs" ref={pageContentRef}>
          {type === 'step' && (
            <div className="content__header">
              <h1 className="content__title">{cleanString(content.pageName)}</h1>
              {content.dateStamp && (
                <time dateTime={content.dateStamp} className="content__date-stamp">
                  {content.dateStamp}
                </time>
              )}
            </div>
          )}
          {displayContent}
        </div>
      </Col>
      <Col xs={{ span: col && col.xs }} lg={{ span: 8 }}>
        <PageContentRHS content={content.pageRHS} keywords={keywords} />
      </Col>
    </>
  ) : (
    <Col xs={{ span: col && col.xs }} lg={{ span: col && col.lg }}>
      <div className="page__content" ref={pageContentRef}>
        {type === 'step' && (
          <div className="content__header">
            <h1 className="content__title">{cleanString(content.pageName)}</h1>
            {content.dateStamp && (
              <time dateTime={content.dateStamp} className="content__date-stamp">
                {content.dateStamp}
              </time>
            )}
          </div>
        )}
        {displayContent}
      </div>
    </Col>
  );
};

type ContentProps = {
  col?: { xs?: number; lg?: number };
  type?: string;
};

const Content: React.FC<ContentProps> = observer(({ col, type }) => {
  const rootStore = useContext(RootStoresContext);
  const location = useLocation();

  const fetchStandardPage = useCallback(() => {
    rootStore.pageStore.setCurrentPage(location.pathname);
  }, [rootStore.pageStore, location.pathname]);

  const fetchKeywordDefinitions = useCallback(() => {
    rootStore.keywordStore.getKeywords(
      `${process.env.REACT_APP_API || ''}${process.env.REACT_APP_BASE_PATH || ''}${process.env
        .REACT_APP_KEYWORDS_API || ''}`
    );
  }, [rootStore.keywordStore]);

  useEffect(() => {
    fetchStandardPage();
    fetchKeywordDefinitions();
  }, [fetchStandardPage, fetchKeywordDefinitions]);
  return (
    <>
      {rootStore.pageStore.isPending && (
        <Col className="hin-loading" xs={{ span: 24 }}>
          <Loading />
        </Col>
      )}
      {rootStore.pageStore.currentPage && (
        <PageContent
          col={col}
          content={rootStore.pageStore.currentPage}
          keywords={rootStore.keywordStore.keywords}
          type={type}
        />
      )}
      {rootStore.pageStore.error && !rootStore.pageStore.currentPage && (
        <ErrorPage
          errorContent={
            rootStore.siteStore.getPageNotFound ||
            `${rootStore.pageStore.error.name} - ${rootStore.pageStore.error.message}`
          }
        />
      )}
    </>
  );
});

export default Content;
